import {
  Field,
  Image,
  ImageField,
  LinkField,
  TextField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import { icon_arrow_right, icon_speech_bubble } from '@gn-audio/icons';
import {
  Button,
  ButtonVariantType,
  ComponentProps,
  DynamicHeading,
  getContrastThemeClass,
  JabraIcon,
  resolveBackgroundStyle,
  useFallbackTranslation,
} from '@gn-audio/jss-components';
import { tailwindConfig } from '@gn-audio/jss-components/tailwind-presets';

import { Eyebrow } from 'common/components/Eyebrow';
import { JabraGallerySlider, JabraGallerySliderItem } from 'common/components/JabraGalerySlider';
import { useSpotTracking } from 'utils/hooks/useSpotTracking';

export type CustomerStorySpotProps = {
  fields: CustomerStorySpot;
} & ComponentProps;

export type CustomerStorySpot = {
  customizerStoryCards: CustomizerStoryCard[];
  title: TextField;
  headline: TextField;
  description: TextField;
  isMainHeading: Field<boolean>;
  primaryButtonText: TextField;
  primaryButtonLink: LinkField;
  primaryButtonAriaLabel?: Field<string>;
  background: TextField;
  trackingSpotName?: Field<string>;
  firstCtaButtonTrackingName?: Field<string>;
};

export type CustomizerStoryCard = {
  image: ImageField;
  headline: TextField;
  description: TextField;
  imagePerson: ImageField;
  namePerson: TextField;
  jobTitlePerson: TextField;
  logoCompany: ImageField;
  identifier: string;
  background: TextField;
};

type CustomerSliderImageProps = {
  item: CustomizerStoryCard;
  index: number;
  active: boolean;
};

const maxDescriptionLength = 350;

const sliderImage = ({ item, index, active }: CustomerSliderImageProps) => {
  const truncatedDescription =
    (item.description?.value as string).length > maxDescriptionLength
      ? (item.description.value as string).substring(0, maxDescriptionLength).trim() + ' ...'
      : item.description?.value;

  const cardThemeClass = getContrastThemeClass(item.background.value as string);
  const cardBackgroundStyle = resolveBackgroundStyle(item.background.value as string) || {
    backgroundColor: tailwindConfig.theme.colors.beige[50],
    color: tailwindConfig.theme.colors.beige[800],
  };

  return (
    <JabraGallerySliderItem
      active={active}
      index={index}
      className="flex flex-shrink-0 flex-row justify-center rounded-3xl p-6 pb-2 pt-0 max-md:container-fluid md:container md:mr-44 md:p-4 md:pt-6 lg:pt-12 2xl:mr-0"
    >
      <div
        style={cardBackgroundStyle}
        className={classNames(
          cardThemeClass,
          'flex max-h-full w-full flex-col rounded-3xl p-0 md:max-w-[1200px] md:flex-row md:p-0 lg:h-[720px]'
        )}
      >
        <div className="hidden basis-1/2 p-0 md:order-1 md:flex md:p-6">
          <Image className="size-full rounded-3xl object-cover" field={item.image} loading="lazy" />
        </div>
        <div className="flex flex-grow basis-1/2 flex-col p-4 text-left md:order-2 md:pb-0 md:pr-8 md:pt-6">
          <div className="order-1 flex size-14 items-center justify-center rounded-full bg-white text-black">
            <div className="flex">
              <JabraIcon className="size-10 filter" Icon={icon_speech_bubble} />
            </div>
          </div>

          <div className="order-2 pt-4">
            <h3 className="heading-2">
              <span
                dangerouslySetInnerHTML={{ __html: sanitize(item.headline?.value as string) }}
              />
            </h3>
          </div>

          <div className="order-3 flex pr-2 md:hidden md:pr-0">
            <Image
              className="size-full rounded-3xl object-cover"
              field={item.image}
              loading="lazy"
            />
          </div>

          <div className="paragraph-large order-4 pb-4 pt-4 md:pt-0">
            <span dangerouslySetInnerHTML={{ __html: sanitize(truncatedDescription as string) }} />
          </div>

          <div className="order-5 mt-auto flex flex-row items-center pb-0 pt-0 align-bottom md:pb-6">
            <Image
              className="size-12 rounded-3xl object-cover"
              field={item.imagePerson}
              loading="lazy"
            />
            <div className="flex flex-col justify-center pl-2 md:pl-2">
              <div className="pb-1 text-sm font-bold leading-4">
                <span>{item.namePerson?.value}</span>
              </div>
              <div className="text-sm font-normal leading-4">
                <span>{item.jobTitlePerson?.value}</span>
              </div>
            </div>
            <div className="ml-auto pl-2">
              <Image className="max-h-full max-w-20" field={item.logoCompany} />
            </div>
          </div>
        </div>
      </div>
    </JabraGallerySliderItem>
  );
};

const CustomerStorySpot: React.FC<CustomerStorySpotProps> = ({ fields }) => {
  const { t } = useFallbackTranslation();
  const {
    customizerStoryCards,
    title,
    headline,
    description,
    isMainHeading,
    primaryButtonText,
    primaryButtonAriaLabel,
    primaryButtonLink,
    background,
    trackingSpotName,
    firstCtaButtonTrackingName,
  } = fields;

  const spotRef = useSpotTracking(trackingSpotName?.value);

  const spotThemeClass = getContrastThemeClass(background.value as string);
  const spotBackgroundStyle = resolveBackgroundStyle(background.value as string);

  const refinedCustomizerStoryCards = customizerStoryCards.filter(
    (customizerStoryCard) => customizerStoryCard.image?.value?.src != null
  );

  return (
    <div
      ref={spotRef}
      data-wt-spot={trackingSpotName?.value}
      style={spotBackgroundStyle}
      className={classNames(spotThemeClass, 'container-fluid')}
    >
      <div className="container mb-0 max-w-6xl !py-0 text-center md:mb-6 md:pl-0 md:pr-0">
        {title?.value && <Eyebrow className="mb-5" text={title.value as string} align="center" />}

        {headline?.value && (
          <DynamicHeading isMainHeading={isMainHeading?.value} className="heading-1">
            <span dangerouslySetInnerHTML={{ __html: sanitize(headline.value as string) }} />
          </DynamicHeading>
        )}
        {description?.value && (
          <div
            className="paragraph-large"
            dangerouslySetInnerHTML={{ __html: sanitize(description.value as string) }}
          />
        )}
        <JabraGallerySlider
          itemsAmountStep={0}
          items={refinedCustomizerStoryCards}
          itemComponent={sliderImage}
          showButtons={true}
          oneItem="max-xl"
          loop={false}
          aria-label={t('system.Cards')}
        />
        {primaryButtonLink?.value?.href && primaryButtonText?.value && (
          <div className="flex justify-center pt-4 md:pt-12 lg:pt-20">
            <Button
              asLink={true}
              href={primaryButtonLink.value.href}
              variant={'primary' as ButtonVariantType}
              size="m"
              iconPlacement="right"
              icon={<JabraIcon Icon={icon_arrow_right} />}
              {...(firstCtaButtonTrackingName?.value && {
                'data-wt-name': firstCtaButtonTrackingName.value,
              })}
              {...(primaryButtonAriaLabel?.value && { 'aria-label': primaryButtonAriaLabel.value })}
            >
              {primaryButtonText.value}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default withDatasourceCheck()(CustomerStorySpot);
